import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  Loader2, 
  Check, 
  ArrowLeft, 
  AlertTriangle,
  MessageCircle,
  RefreshCw,
  Share2
} from 'lucide-react';
import { WhatsAppQRModal } from '@/components/whatsapp/WhatsAppQRModal';

type ConnectionState = 'idle' | 'loading' | 'qr' | 'syncing' | 'connected' | 'expired' | 'error';

function formatPhoneDisplay(phone: string | null | undefined): string {
  if (!phone) return '—';
  const cleaned = phone.replace(/\D/g, '');
  
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  if (cleaned.length === 10) {
    return `+1 (${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  if (cleaned.length === 13 && cleaned.startsWith('55')) {
    return `+${cleaned.slice(0, 2)} ${cleaned.slice(2, 4)} ${cleaned.slice(4, 9)}-${cleaned.slice(9)}`;
  }
  return phone.startsWith('+') ? phone : `+${cleaned}`;
}

const POLL_INTERVAL = 2000;
const QR_TIMEOUT = 120000;

/**
 * WhatsApp Connect page - SHARED session management only.
 * Accessed via Settings > WhatsApp.
 * Individual connections are managed on /profile.
 */
export default function WhatsAppConnect() {
  const navigate = useNavigate();
  
  const [connectionState, setConnectionState] = useState<ConnectionState>('idle');
  const [qrCode, setQrCode] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [timeLeft, setTimeLeft] = useState(0);
  const [showQRModal, setShowQRModal] = useState(false);
  const [sharedPhoneNumber, setSharedPhoneNumber] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  
  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);
  const shouldStopRef = useRef(false);
  const pollFailCountRef = useRef(0);

  // Resolve the active provider's edge function name dynamically
  const resolveEdgeFunction = useCallback(async (): Promise<string> => {
    try {
      const { data, error } = await supabase
        .from('shared_whatsapp_session')
        .select('provider')
        .eq('is_active', true)
        .maybeSingle();
      
      if (error) console.warn('[resolveEdgeFunction] Query error:', error);
      
      const provider = data?.provider || 'wppconnect';
      const fn = provider === 'evolution' ? 'evolution-whatsapp' : 'wppconnect-whatsapp';
      console.log('[resolveEdgeFunction] provider=', provider, '→', fn);
      return fn;
    } catch (err) {
      console.error('[resolveEdgeFunction] Exception:', err);
      return 'wppconnect-whatsapp';
    }
  }, []);

  const edgeFnRef = useRef<string>('wppconnect-whatsapp');

  const callFunction = async (action: string, data: any = {}) => {
    const { data: result, error } = await supabase.functions.invoke(edgeFnRef.current, {
      body: { action, ...data }
    });
    if (error) throw error;
    return result;
  };

  const stopPolling = useCallback(() => {
    shouldStopRef.current = true;
    if (pollingRef.current) clearInterval(pollingRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    pollingRef.current = null;
    countdownRef.current = null;
  }, []);

  // Check shared session status
  const checkSharedStatus = useCallback(async () => {
    try {
      const result = await callFunction('checkSharedStatus');
      
      if (result.connected || result.status === 'connected') {
        setSharedPhoneNumber(result.phoneNumber || null);
        return 'connected';
      }
      
      if (result.sessionExpired || result.needsReconnect) {
        return 'expired';
      }
      
      if (result.status === 'syncing') {
        return 'syncing';
      }
      
      if (result.qrCode) {
        setQrCode(result.qrCode);
        return 'qr';
      }
      
      return result.status || 'disconnected';
    } catch (error) {
      console.error('Shared status check error:', error);
      return 'error';
    }
  }, []);

  // Polling for shared session with grace period
  const POLL_GRACE_FAILURES = 10; // Allow up to 10 consecutive failures (~20s) before expiring

  const startPolling = useCallback(async () => {
    stopPolling();
    shouldStopRef.current = false;
    pollFailCountRef.current = 0;
    
    // Resolve provider BEFORE starting interval to avoid race condition
    const resolvedFn = await resolveEdgeFunction();
    edgeFnRef.current = resolvedFn;
    console.log('[startPolling] Using edge function:', resolvedFn);
    
    const endTime = Date.now() + QR_TIMEOUT;
    setTimeLeft(QR_TIMEOUT);
    
    countdownRef.current = setInterval(() => {
      const remaining = Math.max(0, endTime - Date.now());
      setTimeLeft(remaining);
      
      if (remaining <= 0) {
        stopPolling();
        setConnectionState('expired');
        setErrorMessage('Tempo esgotado. Tente novamente.');
        setShowQRModal(false);
      }
    }, 1000);

    pollingRef.current = setInterval(async () => {
      if (shouldStopRef.current) return;
      
      try {
        const result = await callFunction('sharedStatus');
        
        if (result.connected || result.status === 'connected') {
          pollFailCountRef.current = 0;
          stopPolling();
          setConnectionState('connected');
          setSharedPhoneNumber(result.phoneNumber || null);
          setShowQRModal(false);
          toast.success(`Número central conectado: ${result.phoneNumber || ''}`);
        } else if (result.status === 'syncing') {
          pollFailCountRef.current = 0;
          setConnectionState('syncing');
          setQrCode('');
        } else if (result.qrCode) {
          pollFailCountRef.current = 0;
          setQrCode(result.qrCode);
          setConnectionState('qr');
        } else if (result.status === 'connecting' || result.status === 'qrcode') {
          // Transient state — keep polling, reset fail counter
          pollFailCountRef.current = 0;
        } else if (result.sessionExpired || result.needsReconnect) {
          // Only expire after grace period of consecutive failures
          pollFailCountRef.current++;
          console.log(`[polling] sessionExpired signal, failCount=${pollFailCountRef.current}/${POLL_GRACE_FAILURES}`);
          if (pollFailCountRef.current >= POLL_GRACE_FAILURES) {
            stopPolling();
            setConnectionState('expired');
            setErrorMessage('Sessão expirou. Tente reconectar.');
            setShowQRModal(false);
          }
        }
      } catch (err) {
        pollFailCountRef.current++;
        console.error(`[polling] Error (failCount=${pollFailCountRef.current}):`, err);
        if (pollFailCountRef.current >= POLL_GRACE_FAILURES) {
          stopPolling();
          setConnectionState('error');
          setErrorMessage('Erro persistente ao verificar status. Tente novamente.');
          setShowQRModal(false);
          toast.error('Erro ao verificar status da conexão');
        }
      }
    }, POLL_INTERVAL);
  }, [stopPolling, resolveEdgeFunction]);

  // Check if user is admin and load initial status
  useEffect(() => {
    const init = async () => {
      // Check admin status
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: appUser } = await supabase
          .from('app_users')
          .select('system_role_id, system_roles(name)')
          .eq('auth_user_id', user.id)
          .single();
        
        const roleName = (appUser?.system_roles as any)?.name?.toLowerCase() || '';
        setIsAdmin(roleName === 'admin' || roleName === 'superadmin');
      }
      
      // Resolve provider before any calls
      edgeFnRef.current = await resolveEdgeFunction();
      
      // Check shared session status
      setConnectionState('loading');
      const status = await checkSharedStatus();
      
      if (status === 'connected') {
        setConnectionState('connected');
      } else if (status === 'expired') {
        setConnectionState('expired');
        setErrorMessage('Sessão do número central expirou.');
      } else {
        setConnectionState('idle');
      }
    };
    
    init();
    return () => stopPolling();
  }, [checkSharedStatus, stopPolling]);

  // Admin: Connect shared session
  const handleConnectShared = async () => {
    if (!isAdmin) {
      toast.error('Apenas administradores podem conectar o número central');
      return;
    }
    
    setConnectionState('loading');
    setErrorMessage('');
    toast.info('Iniciando conexão...');
    
    try {
      // Resolve provider dynamically before calling
      edgeFnRef.current = await resolveEdgeFunction();
      console.log('[handleConnectShared] Using edge function:', edgeFnRef.current);
      
      const result = await callFunction('startSharedSession');
      
      // Tratar erro explícito da edge function
      if (result.success === false) {
        const msg = result.error || 'Erro desconhecido ao iniciar sessão';
        setConnectionState('error');
        setErrorMessage(msg);
        toast.error(msg);
        return;
      }
      
      if (result.connected || result.status === 'connected') {
        setConnectionState('connected');
        setSharedPhoneNumber(result.phoneNumber || null);
        toast.success(`Número central conectado: ${result.phoneNumber || ''}`);
        return;
      }
      
      if (result.qrCode) {
        setQrCode(result.qrCode);
        setConnectionState('qr');
        setShowQRModal(true);
        await startPolling();
      } else if (result.status === 'connecting' || result.status === 'qrcode') {
        setConnectionState('loading');
        setShowQRModal(true);
        await startPolling();
      } else {
        setConnectionState('error');
        const msg = result.error || result.message || 'Erro desconhecido ao iniciar sessão';
        setErrorMessage(msg);
        toast.error(msg);
      }
    } catch (error: any) {
      const msg = error.message || 'Erro ao conectar número central';
      setConnectionState('error');
      setErrorMessage(msg);
      toast.error(msg);
    }
  };

  // Admin: Disconnect shared session
  const handleDisconnectShared = async () => {
    if (!isAdmin) {
      toast.error('Apenas administradores podem desconectar o número central');
      return;
    }
    
    setConnectionState('loading');
    stopPolling();
    
    try {
      await callFunction('disconnectShared');
      setQrCode('');
      setSharedPhoneNumber(null);
      setConnectionState('idle');
      toast.success('Número central desconectado');
    } catch (error: any) {
      setConnectionState('error');
      setErrorMessage(error.message || 'Erro ao desconectar');
      toast.error('Erro ao desconectar');
    }
  };

  const handleCloseModal = () => {
    stopPolling();
    setShowQRModal(false);
    setQrCode('');
    if (connectionState !== 'connected') {
      setConnectionState('idle');
    }
  };

  const currentIsConnected = connectionState === 'connected';

  return (
    <div className="bg-background p-4 md:p-8">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header com botão voltar */}
        <Button
          variant="ghost"
          onClick={() => navigate('/Settings')}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar para Configurações
        </Button>

        {/* Card Principal */}
        <Card className="overflow-hidden">
          <CardContent className="p-0">
            {/* Header do App */}
            <div className="flex items-center justify-between p-6 border-b">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-[#25D366] flex items-center justify-center">
                  <Share2 className="h-7 w-7 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-foreground">WhatsApp Central</h1>
                  <p className="text-sm text-muted-foreground">
                    Número compartilhado para toda a equipe
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                {currentIsConnected ? (
                  <>
                    <Badge variant="outline" className="bg-[#25D366]/10 text-[#25D366] border-[#25D366]/30 px-3 py-1">
                      <Check className="h-3.5 w-3.5 mr-1.5" />
                      Conectado
                    </Badge>
                    {isAdmin && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleDisconnectShared}
                        className="text-destructive border-destructive/30 hover:bg-destructive/10"
                      >
                        Desconectar
                      </Button>
                    )}
                  </>
                ) : (
                  <>
                    <Badge variant="outline" className="bg-muted text-muted-foreground px-3 py-1">
                      Não conectado
                    </Badge>
                    {isAdmin && (
                      <Button
                        onClick={handleConnectShared}
                        disabled={connectionState === 'loading'}
                        className="bg-[#25D366] hover:bg-[#128C7E] text-white"
                      >
                        {connectionState === 'loading' ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Conectando...
                          </>
                        ) : (
                          'Conectar'
                        )}
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Conteúdo - Info quando conectado */}
            {currentIsConnected && (
              <div className="p-6 space-y-6">
                <div className="rounded-lg border overflow-hidden">
                  <div className="bg-muted/50 px-4 py-3 border-b">
                    <h3 className="text-sm font-medium text-foreground">Número Central</h3>
                  </div>
                  <div className="divide-y">
                    <div className="grid grid-cols-3 gap-4 px-4 py-3 text-sm">
                      <div className="text-muted-foreground">Número</div>
                      <div className="col-span-2 font-medium font-mono">
                        {formatPhoneDisplay(sharedPhoneNumber)}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4 px-4 py-3 text-sm">
                      <div className="text-muted-foreground">Status</div>
                      <div className="col-span-2 flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-[#25D366]" />
                        <span className="font-medium">Online</span>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4 px-4 py-3 text-sm">
                      <div className="text-muted-foreground">Tipo</div>
                      <div className="col-span-2 flex items-center gap-2">
                        <Share2 className="w-4 h-4 text-muted-foreground" />
                        <span>Compartilhado (divus-central)</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 dark:bg-blue-950/30 rounded-lg p-4">
                  <div className="flex gap-3">
                    <MessageCircle className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium text-blue-700 dark:text-blue-400">
                        Número Central Ativo
                      </p>
                      <p className="text-blue-600/80 dark:text-blue-300/80 mt-1">
                        Todas as mensagens enviadas pelo sistema usarão este número.
                        Para conectar seu número pessoal, acesse seu Perfil.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Conteúdo - Quando não conectado */}
            {!currentIsConnected && (
              <div className="p-6">
                {connectionState === 'expired' && (
                  <div className="bg-amber-50 dark:bg-amber-950/30 rounded-lg p-4 mb-4">
                    <div className="flex gap-3">
                      <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                      <div className="text-sm">
                        <p className="font-medium text-amber-700 dark:text-amber-400">
                          Sessão Expirada
                        </p>
                        <p className="text-amber-600/80 dark:text-amber-300/80 mt-1">
                          {errorMessage || 'O número central precisa ser reconectado.'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {connectionState === 'error' && errorMessage && (
                  <div className="bg-destructive/10 rounded-lg p-4 mb-4">
                    <div className="flex gap-3">
                      <AlertTriangle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                      <div className="text-sm">
                        <p className="font-medium text-destructive">Erro</p>
                        <p className="text-destructive/80 mt-1">{errorMessage}</p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="bg-muted/50 rounded-lg p-4">
                  <div className="flex gap-3">
                    <Share2 className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium text-foreground">
                        Número Central Desconectado
                      </p>
                      <p className="text-muted-foreground mt-1">
                        {isAdmin 
                          ? 'Clique em "Conectar" para vincular o número central da empresa.'
                          : 'O número central não está conectado. Contate um administrador.'
                        }
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <MessageCircle className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div className="text-sm text-muted-foreground">
                <p className="font-medium text-foreground mb-1">Sobre o WhatsApp Central</p>
                <p>
                  Este é o número compartilhado usado por toda a equipe para enviar mensagens.
                  Para usar seu próprio número pessoal, configure em seu <strong>Perfil</strong>.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* QR Modal for Admin */}
      <WhatsAppQRModal
        open={showQRModal}
        onClose={handleCloseModal}
        qrCode={qrCode}
        timeLeft={timeLeft}
        maxTime={QR_TIMEOUT}
        isLoading={connectionState === 'loading'}
        isSyncing={connectionState === 'syncing'}
      />
    </div>
  );
}
